import {Injectable} from "@angular/core";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {Subject, take} from "rxjs";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Globals} from "../../../core/services/globals";
import {DomSanitizer} from "@angular/platform-browser";
import {ViewportScroller} from "@angular/common";
import {TrackingService} from "../../../core/services/tracking.service";
import {NavbarBehaviorService} from "../../../core/services/navbar-behavior.service";
import {SEOService} from '../../../core/services/seo.service';
import {Data, Listing} from '../../../core/models/listing';
import {switchMap, takeUntil} from "rxjs/operators";
import {ObservableInput} from "rxjs/internal/types";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {ListingsService} from "../../../core/services/listings-service.interface";
import {NgZone} from '@angular/core';

@Injectable()
export abstract class BaseListingDetailsComponent {
  selectedListing: Listing | undefined;
  loadedBln = false;
  currentScreenSize: string | undefined;
  protected destroyed$ = new Subject<void>(); // For cleanup
  bsModalRef: BsModalRef | undefined;

  // Responsive display map for shared behavior
  protected displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge'],
  ]);
  protected trackingURLStr = '/classes/REP_RETSTracking.php'; // URL to web api

  private supportsWebP = false;
  protected headerImageLoaded = false;
  protected headerImageStyle: any = {};
  protected headerImageUrl = '';


  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected http: HttpClient,
    protected listingsService: ListingsService,
    protected SEOService: SEOService,
    public globals: Globals,
    protected _sanitizer: DomSanitizer,
    protected scroller: ViewportScroller,
    protected trackingService: TrackingService,
    protected breakpointObserver: BreakpointObserver,
    protected navbarService: NavbarBehaviorService,
    protected modalService: BsModalService,
    protected zone: NgZone
  ) {
    this.checkWebPSupport().then(supported => {
      this.supportsWebP = supported;
    });
  }

  private checkWebPSupport(): Promise<boolean> {
    // On the server, the Image API is not available
    if (typeof Image === 'undefined') {
      return Promise.resolve(false);
    }
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(img.width > 0 && img.height > 0);
      img.onerror = () => resolve(false);
      img.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    });
  }

  /**
   * Initialize dashboard base functionality - should be called in child components' ngOnInit
   */
  protected initializeBaseListingDetails(): void {
    console.log('initializeBaseListingDetails called');
    console.log('currentRoute', this.router.url);
    console.log('routeStr', this.globals.routeStr);
    // Store the current route as previous route before moving to details
    const currentRoute = this.router.url;
    if (currentRoute && currentRoute !== this.globals.routeStr) {
      // Store current route before updating
      const oldRoute = this.globals.routeStr;
      this.globals.routeStr = currentRoute;
      // Only update previous route if we have a valid old route
      if (oldRoute) {
        console.log('oldRoute', oldRoute);
        this.globals.previousRouteStr = currentRoute;
      }
    }

    // Apply navbar adjustments
    this.setupNavbar();

    // Detect screen changes
    this.setupBreakpointObserver();

    // fetch the listing
    this.fetchlisting();
  }

  /**
   * Clean up base dashboard resources - should be called in child components' ngOnDestroy
   */
  protected cleanupBaseDashboard(): void {
    // Store current route as previous route before component is destroyed
    const currentRoute = this.router.url;
    // Only update if we're navigating away from details page
    if (currentRoute.includes('/fulldetails/')) {
      this.globals.previousRouteStr = currentRoute;
    }


    // Clean up subscriptions
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Setup the navbar with proper spacing and styling
   */
  protected setupNavbar(): void {
    // initialize the navbar service
    this.navbarService.initialize();

    // Manually call updateHeaderOnScroll once to set the initial state
    this.navbarService.updateHeaderOnScroll();
  }


  // Common shared methods that can be used by both dashboards
  /**
   * Update the header image based on the selected listing and current screen size
   * This method is called whenever the screen size changes or a new listing is selected
   */
  protected updateHeaderImage(): void {
    if (!this.selectedListing?.listingMedia?.data[0]?.url) {
      return;
    }

    const size = (this.currentScreenSize === 'Large' || this.currentScreenSize === 'XLarge') ? 'large' : 'small';
    this.headerImageUrl = this.getTransformedImageUrl(this.selectedListing.listingMedia.data[0].url, size);
  }

  /**
   * Shared Breakpoint Observer Setup: Detect screen sizes and handle shared logic
   */
  protected setupBreakpointObserver(): void {
    // Avoid initializing breakpoint observer on the server to keep SSR stable
    if (typeof window === 'undefined') {
      return;
    }
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize =
              this.displayNameMap.get(query) ?? 'Unknown';
            this.updateHeaderImage();

          }
        }
        //console.log('Screen size updated:', this.currentScreenSize);
      });
  }


  protected async fetchlisting(): Promise<void> {
    // console.log('detail indirect');

    // Safety timeout: if nothing arrives within 8s, show the shell instead of spinning forever
    let safetyTimeout: any = null;
    // Avoid creating timers on the server during SSR
    if (typeof window !== 'undefined') {
      safetyTimeout = setTimeout(() => {
        if (!this.loadedBln) {
          console.warn('Details safety timeout reached — showing fallback UI.');
          this.loadedBln = true;
          this.headerImageLoaded = true;
          this.headerImageStyle = {
            'background-color': '#f5f5f5',
            'opacity': 1
          };
        }
      }, 8000);
    }

    // fork, if this is a non etobicoke search then use the peel halton db to search for the listing
    // fork, if an actual mls number has been defined in the route then get the listing
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap): ObservableInput<Data> => {
            const MLSNum = params.get('MLSNum') || '';
            console.log('Fetching listing details for MLS:', MLSNum);
            return this.listingsService.getListing(MLSNum);
          }
        ),
        take(1)
      )
      .subscribe({
        next: async (response: Data) => {
          try {
            this.selectedListing = this.fixImages(response.data[0] as Listing);

            // Preload the header image before setting loadedBln to true
            await this.preloadHeaderImage();

            this.loadedBln = true;
            // track the event (browser only)
            if (typeof window !== 'undefined') {
              this.trackEvent({_eventStr: 'view-listing'});
              this.trackingService.trackUserJourney(this.selectedListing.id, 'view', 'Client Viewed Listing');
            }
            // set the seo data
            this.setSEOTags();

            this.onListingFetched(this.selectedListing);
          } finally {
            if (safetyTimeout) {
              clearTimeout(safetyTimeout);
              safetyTimeout = null;
            }
          }
        },
        error: (error) => {
          console.error('Failed to fetch listing details:', error);
          // Fail-safe: allow page shell to render and avoid endless blank state
          this.loadedBln = true;
          this.headerImageLoaded = true;
          this.headerImageStyle = {
            'background-color': '#f5f5f5',
            'opacity': 1
          };
          if (safetyTimeout) {
            clearTimeout(safetyTimeout);
            safetyTimeout = null;
          }
        }
      });
  }

  protected getTransformedImageUrl(imageUrl: string, size: 'large' | 'small'): string {
    if (!imageUrl) {
      return '';
    }

    console.log('getTransformedImageUrl', imageUrl, size);

    try {
      // Check if URL is in the Muskoka image cache format
      if (imageUrl.includes('https://d2hg257h9htd0r.cloudfront.net/muskoka_image_cache/resized/medium-optimized/')) {
        return imageUrl;
      }
      console.log('getTransformedImageUrl', imageUrl);

      // Handle standard cloudfront URLs
      const url = new URL(imageUrl);
      const format = this.supportsWebP ? 'webp' : 'jpg';
      const filename = url.pathname.split('/').pop();

      const dimensions = size === 'large' ? '2000x2000' : '800x800';
      const quality = size === 'large' ? '80' : '85';

      return `https://d3hnn994gxsthv.cloudfront.net/fit-in/${dimensions}/filters:format(${format}):quality(${quality})/${filename}`;
    } catch (error) {
      console.error('Error transforming image URL:', error);
      return imageUrl;
    }
  }


  protected async preloadHeaderImage(): Promise<void> {
    if (!this.selectedListing?.listingMedia?.data[0]?.url) {
      console.warn('No image URL available for preloading');
      return;
    }

    // SSR guard: avoid using Image API on the server
    if (typeof Image === 'undefined') {
      this.updateHeaderImage();
      this.headerImageStyle = this.headerImageUrl
        ? {
            'background-image': `url('${this.headerImageUrl}')`,
            'background-position': 'center',
            'background-size': 'cover',
            'background-repeat': 'no-repeat',
            'opacity': 1
          }
        : {
            'background-color': '#f5f5f5',
            'opacity': 1
          };
      this.headerImageLoaded = true;
      return;
    }

    try {
      this.updateHeaderImage();

      return new Promise<void>((resolve) => {
        const img = new Image();
        let aborted = false;

        const cleanup = () => {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        };

        img.onload = () => {
          if (aborted) return;
          this.zone.run(() => {
            this.headerImageStyle = {
              'background-image': `url('${this.headerImageUrl}')`,
              'background-position': 'center',
              'background-size': 'cover',
              'background-repeat': 'no-repeat',
              'opacity': 1,
              'transition': 'opacity 0.3s ease-in'
            };
            this.headerImageLoaded = true;
            cleanup();
            resolve();
          });
        };

        img.onerror = () => {
          if (aborted) return;
          console.error('Error loading header image');
          this.zone.run(() => {
            // Set a default style or fallback image on error
            this.headerImageStyle = {
              'background-color': '#f5f5f5',  // fallback background color
              'opacity': 1
            };
            this.headerImageLoaded = true;
            cleanup();
            resolve();
          });
        };

        this.destroyed$.pipe(take(1)).subscribe(() => {
          aborted = true;
          cleanup();
          resolve();
        });

        // Add error handling for URL
        if (!this.headerImageUrl) {
          img.onerror(new Event('error'));
          return;
        }

        img.src = this.headerImageUrl;
      });
    } catch (error) {
      console.error('Error in preloadHeaderImage:', error);
      this.headerImageLoaded = true;
      this.headerImageStyle = {
        'background-color': '#f5f5f5',
        'opacity': 1
      };
    }
  }


  public trackEvent(obj: { _eventStr: string }) {
    return;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded;',
      'X-Requested-With': 'XMLHttpRequest',
    });
    const options = {
      headers: headers,
      params: this.generateEventTrackingData({obj: obj}),
    };
    const paramsObj = this.generateEventTrackingData({obj: obj});
    const params = new HttpParams({
      fromObject: paramsObj,
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };
    // track the event
    return this.http
      .post<Listing[]>(this.trackingURLStr, params.toString(), httpOptions)
      .subscribe(
        (response) => {
          // console.log(response.json());
        },
        (error) => {
          this.handleError(error); // Centralized error handling
        },
      );
  }

  private generateEventTrackingData({obj}: { obj: any }): any {
    // define properties
    const emailStr = this.globals.emailStr;
    const userIDStr = this.globals.userIDStr;
    const ipAddrStr = this.globals.ipAddrStr;
    const sidStr = this.globals.sidStr;
    const idStr = this.globals.idStr;
    const eventStr = obj._eventStr;
    const eventFullStr = this.generateEventFullString();
    // define properties
    const JSONObj = {
      idStr: idStr,
      emailStr: '',
      userIDStr: '',
      ipAddrStr: ipAddrStr,
      sessionIDStr: '',
      eventStr: eventStr,
      eventFullStr: eventFullStr,
      viewStr: 'list-view',
    };
    if (emailStr != '') {
      JSONObj.emailStr = emailStr;
    } else {
      delete (JSONObj as any)['emailStr'];
    }
    if (userIDStr != '') {
      JSONObj.userIDStr = userIDStr;
    } else {
      delete (JSONObj as any)['userIDStr'];
    }
    if (sidStr != '') {
      JSONObj.sessionIDStr = sidStr;
    } else {
      delete (JSONObj as any)['sessionIDStr'];
    }
    return JSONObj;
  }

  private generateEventFullString() {
    // define properties
    if (this.selectedListing == undefined) {
      return;
    }
    const MLSNum = this.selectedListing.MLSNUM;
    const propertyTypeStr = this.globals.propertyTypeStr;
    const eventFullObj = {
      MLSNum: MLSNum,
      propertyTypeStr: propertyTypeStr,
    };
    // return the json object as a string
    return JSON.stringify(eventFullObj);
  }

  private fixImages(listingDetailsCls: Listing) {
    const format = this.supportsWebP ? 'webp' : 'jpg';
    const imageArr = listingDetailsCls.listingMedia.data;

    for (let i = 0; i < imageArr.length; i++) {
      try {
        let urlStr = imageArr[i].url;

        if (urlStr.includes('dhauv7lm2gh1r.cloudfront.net/rets_image_cache/muskoka_image_cache/')) {
          // Handle Muskoka image cache URLs
          urlStr = urlStr.replace(
            'https://dhauv7lm2gh1r.cloudfront.net/rets_image_cache/muskoka_image_cache/',
            'https://d2hg257h9htd0r.cloudfront.net/muskoka_image_cache/resized/medium-optimized/'
          );
        } else {
          // Handle standard cloudfront URLs
          const url = new URL(urlStr);
          const filename = url.pathname.split('/').pop();

          // Construct optimized URL with proper structure
          urlStr = `https://d3hnn994gxsthv.cloudfront.net/fit-in/2000x2000/filters:format(${format}):quality(80)/${filename}`;
        }

        // Update the array item
        listingDetailsCls.listingMedia.data[i].url = urlStr;

      } catch (error) {
        console.error('Error processing image URL:', error);
        // Keep original URL if there's an error
      }
    }

    return listingDetailsCls;
  }


  protected fixThumbnailImage(urlStr: string) {
    if (!urlStr) {
      return '';
    }

    try {
      // Check if URL has already been converted to medium-optimized format
      if (urlStr.includes('muskoka_image_cache/resized/medium-optimized/')) {
        return urlStr;
      }

      // Check if URL is in the Muskoka image cache format
      if (urlStr.includes('dhauv7lm2gh1r.cloudfront.net/rets_image_cache/muskoka_image_cache/')) {
        return urlStr.replace(
          'https://dhauv7lm2gh1r.cloudfront.net/rets_image_cache/muskoka_image_cache/',
          'https://d2hg257h9htd0r.cloudfront.net/muskoka_image_cache/resized/medium-optimized/'
        );
      }

      // Handle standard cloudfront URLs
      const format = this.supportsWebP ? 'webp' : 'jpg';
      const url = new URL(urlStr);
      const filename = url.pathname.split('/').pop();

      // Use 380x219 dimensions for gallery thumbnails to match the container size
      return `https://d3hnn994gxsthv.cloudfront.net/fit-in/400x400/filters:format(${format}):quality(80)/${filename}`;

    } catch (error) {
      console.error('Error processing thumbnail URL:', error);
      return urlStr;
    }
  }


  protected setSEOTags() {
    // define properties
    const listingDetailsCls = this.selectedListing;
    if (listingDetailsCls == undefined) {
      return;
    }
    const addressStr = listingDetailsCls.listingDetail?.address ?? listingDetailsCls.fullAddress;
    const addressHyphenStr = addressStr.replaceAll(' ', '-');
    const cityStr = listingDetailsCls.municipality;
    const cityHypenStr = listingDetailsCls.municipality.replaceAll(' ', '-');
    const stateStr = 'ON';
    const MLSNum = listingDetailsCls.MLSNUM;
    const titleStr =
      addressStr +
      ', ' +
      cityStr +
      ', ' +
      stateStr +
      ' ' +
      ' (MLS #' +
      MLSNum +
      ') :: Cayman Marshall International Realty Inc., Brokerage';
    const descriptionStr =
      'Residential property for sale in ' +
      cityStr +
      ', ' +
      stateStr +
      ' (MLS #' +
      MLSNum +
      '). Learn more from Cayman Marshall.';
    const keywordsStr =
      'Real Estate, Houses, ' +
      addressStr +
      ', ' +
      cityStr +
      ', ' +
      stateStr +
      ', Residential, Full Service, $/Sf/Month, Yes, No';
    // set the SEO data
    this.SEOService.setMetaTags({
      _titleStr: titleStr,
      _descriptionStr: descriptionStr,
      _urlStr:
        'https://www.' +
        this.globals.hostnameStr +
        '/search/fulldetails/' +
        addressHyphenStr +
        '/' +
        cityHypenStr +
        '/' +
        MLSNum,
      _keywordsStr: keywordsStr,
    });
  }

  protected getBackground(image: string) {
    if (!image) {
      console.warn('No image URL provided');
      return 'none';
    }

    try {
      // Parse the URL to get the path segments
      const url = new URL(image);
      const format = this.supportsWebP ? 'webp' : 'jpg';

      // Extract the filename from the original URL
      const filename = url.pathname.split('/').pop();

      // Construct new URL with the working path structure:
      // first fit-in, then dimensions, then filters
      const urlStr = `https://d3hnn994gxsthv.cloudfront.net/fit-in/2000x2000/filters:format(${format}):quality(80)/${filename}`;

      console.log('Transformed URL:', urlStr);
      return this._sanitizer.bypassSecurityTrustStyle(`url('${urlStr}')`);
    } catch (error) {
      console.error('Error transforming image URL:', error);
      return this._sanitizer.bypassSecurityTrustStyle(`url('${image}')`);
    }
  }


  protected getBackgroundSmall(image: string) {
    if (!image) {
      console.warn('No image URL provided');
      return 'none';
    }

    try {
      // Parse the URL to get the path segments
      const url = new URL(image);
      const format = this.supportsWebP ? 'webp' : 'jpg';

      // Extract the filename from the original URL
      const filename = url.pathname.split('/').pop();

      // Construct new URL with the working path structure:
      // first fit-in, then dimensions, then filters
      const urlStr = `https://d3hnn994gxsthv.cloudfront.net/fit-in/800x800/filters:format(${format}):quality(85)/${filename}`;

      console.log('Transformed URL:', urlStr);
      return this._sanitizer.bypassSecurityTrustStyle(`url('${urlStr}')`);
    } catch (error) {
      console.error('Error transforming image URL:', error);
      return this._sanitizer.bypassSecurityTrustStyle(`url('${image}')`);
    }
  }

  protected gotoHashtag(fragment: string) {
    // console.log(fragment);
    this.scroller.scrollToAnchor(fragment);
    // el.scrollIntoView({behavior: 'smooth'});
  }


  protected handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  /**
   * Abstract method: Child classes can handle listings after they are fetched
   * @param listings Fetched listings array
   */
  protected abstract onListingFetched(listings: Listing): void;

}
